import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, CheckCircle, XCircle, Users, DollarSign, Package, Save, AlertTriangle, Building2, School, Calendar } from 'lucide-react';
import axios from 'axios';

const SubscriptionPlans = () => {
  const [activeTab, setActiveTab] = useState('tenant'); // 'tenant' or 'group'
  const [plans, setPlans] = useState([]);
  const [groupPlans, setGroupPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Tenant plan form
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    price_monthly: 0,
    renewal_price_monthly: 0,
    price_yearly: 0,
    renewal_price_yearly: 0,
    gst_percent: 18,
    gst_included: false,
    currency: '₹',
    max_students: 100,
    max_staff: 20,
    max_yearly_admissions: -1,
    max_yearly_staff: -1,
    max_configurable_modules: 0,
    is_active: true
  });

  // Group plan form
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    code: '',
    description: '',
    price_monthly: 0,
    renewal_price_monthly: 0,
    price_yearly: 0,
    renewal_price_yearly: 0,
    gst_percent: 18,
    gst_included: false,
    currency: '₹',
    max_colleges: 5,
    max_students: -1,
    max_staff: -1,
    max_yearly_admissions: -1,
    max_yearly_staff: -1,
    is_active: true
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPlans();
    fetchGroupPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/subscription-plans/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/groups/subscription-plans/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setGroupPlans(response.data || []);
    } catch (error) {
      console.error('Error fetching group plans:', error);
    }
  };

  // ============ Tenant Plan Functions ============
  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || '',
      code: plan.code || '',
      description: plan.description || '',
      price_monthly: plan.price_monthly || 0,
      renewal_price_monthly: plan.renewal_price_monthly || 0,
      price_yearly: plan.price_yearly || 0,
      renewal_price_yearly: plan.renewal_price_yearly || 0,
      gst_percent: plan.gst_percent ?? 18,
      gst_included: plan.gst_included ?? false,
      currency: plan.currency || '₹',
      max_students: plan.max_students || 100,
      max_staff: plan.max_staff || 20,
      max_yearly_admissions: plan.max_yearly_admissions ?? -1,
      max_yearly_staff: plan.max_yearly_staff ?? -1,
      max_configurable_modules: plan.max_configurable_modules ?? 0,
      is_active: plan.is_active !== false
    });
    setShowModal(true);
  };

  const handleDeleteClick = (plan) => {
    setDeletingPlan(plan);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPlan) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = activeTab === 'tenant' 
        ? `${API_URL}/api/tenants/subscription-plans/${deletingPlan.id}`
        : `${API_URL}/api/groups/subscription-plans/${deletingPlan.id}`;
      
      await axios.delete(endpoint, { headers: { 'Authorization': `Bearer ${token}` } });
      alert('Plan deleted successfully!');
      setShowDeleteModal(false);
      setDeletingPlan(null);
      activeTab === 'tenant' ? fetchPlans() : fetchGroupPlans();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingPlan) {
        await axios.put(
          `${API_URL}/api/tenants/subscription-plans/${editingPlan.id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        alert('Plan updated successfully!');
      } else {
        await axios.post(
          `${API_URL}/api/tenants/subscription-plans/create`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        alert('Plan created successfully!');
      }
      
      setShowModal(false);
      setEditingPlan(null);
      resetForm();
      fetchPlans();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', code: '', description: '',
      price_monthly: 0, renewal_price_monthly: 0,
      price_yearly: 0, renewal_price_yearly: 0, 
      gst_percent: 18, gst_included: false,
      currency: '₹',
      max_students: 100, max_staff: 20, 
      max_yearly_admissions: -1, max_yearly_staff: -1,
      max_configurable_modules: 0, is_active: true
    });
  };

  const resetGroupForm = () => {
    setGroupFormData({
      name: '', code: '', description: '',
      price_monthly: 0, renewal_price_monthly: 0,
      price_yearly: 0, renewal_price_yearly: 0,
      gst_percent: 18, gst_included: false,
      currency: '₹',
      max_colleges: 5, max_students: -1, max_staff: -1,
      max_yearly_admissions: -1, max_yearly_staff: -1,
      is_active: true
    });
  };

  // ============ Group Plan Functions ============
  const handleGroupEdit = (plan) => {
    setEditingPlan(plan);
    setGroupFormData({
      name: plan.name || '',
      code: plan.code || '',
      description: plan.description || '',
      price_monthly: plan.price_monthly || 0,
      renewal_price_monthly: plan.renewal_price_monthly || 0,
      price_yearly: plan.price_yearly || 0,
      renewal_price_yearly: plan.renewal_price_yearly || 0,
      gst_percent: plan.gst_percent ?? 18,
      gst_included: plan.gst_included ?? false,
      currency: plan.currency || '₹',
      max_colleges: plan.max_colleges || 5,
      max_students: plan.max_students ?? -1,
      max_staff: plan.max_staff ?? -1,
      max_yearly_admissions: plan.max_yearly_admissions ?? -1,
      max_yearly_staff: plan.max_yearly_staff ?? -1,
      is_active: plan.is_active !== false
    });
    setShowModal(true);
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingPlan) {
        await axios.put(
          `${API_URL}/api/groups/subscription-plans/${editingPlan.id}`,
          groupFormData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        alert('Group plan updated successfully!');
      } else {
        await axios.post(
          `${API_URL}/api/groups/subscription-plans/create`,
          groupFormData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        alert('Group plan created successfully!');
      }
      
      setShowModal(false);
      setEditingPlan(null);
      resetGroupForm();
      fetchGroupPlans();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const openNewPlanModal = () => {
    setEditingPlan(null);
    if (activeTab === 'tenant') {
      resetForm();
    } else {
      resetGroupForm();
    }
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="subscription-plans">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="text-gray-500">Manage pricing plans for Tenants and Groups</p>
        </div>
        <button
          onClick={openNewPlanModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          New Plan
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('tenant')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
            activeTab === 'tenant' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Building2 size={20} />
          Tenant Plans ({plans.length})
        </button>
        <button
          onClick={() => setActiveTab('group')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
            activeTab === 'group' 
              ? 'text-purple-600 border-b-2 border-purple-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <School size={20} />
          Group Plans ({groupPlans.length})
        </button>
      </div>

      {/* Tenant Plans Tab */}
      {activeTab === 'tenant' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-white rounded-xl shadow-lg overflow-hidden border hover:shadow-xl transition-shadow">
              <div className={`p-4 ${plan.is_active ? 'bg-blue-600' : 'bg-gray-400'} text-white`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  {plan.is_active ? (
                    <CheckCircle size={20} />
                  ) : (
                    <XCircle size={20} />
                  )}
                </div>
                <p className="text-sm opacity-80">{plan.code?.toUpperCase()}</p>
              </div>
              
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">{plan.description || 'No description'}</p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">First Time (Monthly)</span>
                    <span className="text-base font-semibold text-emerald-600">{plan.currency}{(plan.price_monthly || 0)?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">First Time (Yearly)</span>
                    <span className="text-xl font-bold text-green-600">{plan.currency}{plan.price_yearly?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Renewal (Monthly)</span>
                    <span className="text-base font-semibold text-indigo-600">{plan.currency}{(plan.renewal_price_monthly || plan.price_monthly || 0)?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Renewal (Yearly)</span>
                    <span className="text-xl font-bold text-blue-600">{plan.currency}{(plan.renewal_price_yearly || plan.price_yearly || 0)?.toLocaleString('en-IN')}</span>
                  </div>
                  {plan.gst_percent > 0 && (
                    <div className="text-xs text-gray-500 text-right">
                      {plan.gst_included ? `(Incl. ${plan.gst_percent}% GST)` : `+ ${plan.gst_percent}% GST`}
                    </div>
                  )}
                  
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Users size={16} className="text-gray-400" />
                      <span>Max Students: {plan.max_students === -1 ? 'Unlimited' : plan.max_students}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users size={16} className="text-gray-400" />
                      <span>Max Staff: {plan.max_staff === -1 ? 'Unlimited' : plan.max_staff}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <Calendar size={16} className="text-blue-400" />
                      <span>Yearly Admissions: {plan.max_yearly_admissions === -1 || plan.max_yearly_admissions === undefined ? 'Unlimited' : plan.max_yearly_admissions}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <Calendar size={16} className="text-blue-400" />
                      <span>Yearly Staff: {plan.max_yearly_staff === -1 || plan.max_yearly_staff === undefined ? 'Unlimited' : plan.max_yearly_staff}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-purple-600">
                      <Users size={16} className="text-purple-400" />
                      <span>Extra Modules: {plan.max_configurable_modules === -1 || plan.max_configurable_modules === undefined ? 'Unlimited' : plan.max_configurable_modules === 0 ? 'None (Fixed only)' : plan.max_configurable_modules}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <button
                    onClick={() => handleEdit(plan)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                  >
                    <Edit size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteClick(plan)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Group Plans Tab */}
      {activeTab === 'group' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groupPlans.length === 0 ? (
            <div className="col-span-3 text-center py-12 bg-gray-50 rounded-xl">
              <Package size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-4">No custom group plans created yet.</p>
              <p className="text-sm text-gray-400 mb-4">Default plans are being used. Create custom plans to override.</p>
              <button
                onClick={openNewPlanModal}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Create First Plan
              </button>
            </div>
          ) : (
            groupPlans.map((plan) => (
              <div key={plan.id} className="bg-white rounded-xl shadow-lg overflow-hidden border hover:shadow-xl transition-shadow">
                <div className={`p-4 ${plan.is_active ? 'bg-purple-600' : 'bg-gray-400'} text-white`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                    {plan.is_active ? (
                      <CheckCircle size={20} />
                    ) : (
                      <XCircle size={20} />
                    )}
                  </div>
                  <p className="text-sm opacity-80">{plan.code?.toUpperCase()}</p>
                </div>
                
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-4">{plan.description || 'No description'}</p>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">First Time (Monthly)</span>
                      <span className="text-base font-semibold text-emerald-600">{plan.currency}{(plan.price_monthly || 0)?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">First Time (Yearly)</span>
                      <span className="text-xl font-bold text-green-600">{plan.currency}{plan.price_yearly?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Renewal (Monthly)</span>
                      <span className="text-base font-semibold text-indigo-600">{plan.currency}{(plan.renewal_price_monthly || plan.price_monthly || 0)?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Renewal (Yearly)</span>
                      <span className="text-xl font-bold text-blue-600">{plan.currency}{(plan.renewal_price_yearly || plan.price_yearly || 0)?.toLocaleString('en-IN')}</span>
                    </div>
                    {plan.gst_percent > 0 && (
                      <div className="text-xs text-gray-500 text-right">
                        {plan.gst_included ? `(Incl. ${plan.gst_percent}% GST)` : `+ ${plan.gst_percent}% GST`}
                      </div>
                    )}
                    
                    <div className="border-t pt-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <School size={16} className="text-gray-400" />
                        <span>Max Colleges: {plan.max_colleges === -1 ? 'Unlimited' : plan.max_colleges}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Users size={16} className="text-gray-400" />
                        <span>Max Students: {plan.max_students === -1 ? 'Unlimited' : plan.max_students}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Users size={16} className="text-gray-400" />
                        <span>Max Staff: {plan.max_staff === -1 ? 'Unlimited' : plan.max_staff}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-purple-600">
                        <Calendar size={16} className="text-purple-400" />
                        <span>Yearly Admissions: {plan.max_yearly_admissions === -1 || plan.max_yearly_admissions === undefined ? 'Unlimited' : plan.max_yearly_admissions}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-purple-600">
                        <Calendar size={16} className="text-purple-400" />
                        <span>Yearly Staff: {plan.max_yearly_staff === -1 || plan.max_yearly_staff === undefined ? 'Unlimited' : plan.max_yearly_staff}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <button
                      onClick={() => handleGroupEdit(plan)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200"
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(plan)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className={`p-4 ${activeTab === 'tenant' ? 'bg-blue-600' : 'bg-purple-600'} text-white rounded-t-xl`}>
              <h2 className="text-xl font-bold">
                {editingPlan ? 'Edit' : 'Create'} {activeTab === 'tenant' ? 'Tenant' : 'Group'} Plan
              </h2>
            </div>
            
            <form onSubmit={activeTab === 'tenant' ? handleSubmit : handleGroupSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
                  <input
                    type="text"
                    required
                    value={activeTab === 'tenant' ? formData.name : groupFormData.name}
                    onChange={(e) => activeTab === 'tenant' 
                      ? setFormData({ ...formData, name: e.target.value })
                      : setGroupFormData({ ...groupFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Premium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Code *</label>
                  <input
                    type="text"
                    required
                    value={activeTab === 'tenant' ? formData.code : groupFormData.code}
                    onChange={(e) => activeTab === 'tenant'
                      ? setFormData({ ...formData, code: e.target.value.toLowerCase() })
                      : setGroupFormData({ ...groupFormData, code: e.target.value.toLowerCase() })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., premium"
                    disabled={!!editingPlan}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={activeTab === 'tenant' ? formData.description : groupFormData.description}
                  onChange={(e) => activeTab === 'tenant'
                    ? setFormData({ ...formData, description: e.target.value })
                    : setGroupFormData({ ...groupFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  placeholder="Plan description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Time Price (Monthly ₹)</label>
                  <input
                    type="number"
                    value={activeTab === 'tenant' ? formData.price_monthly : groupFormData.price_monthly}
                    onChange={(e) => activeTab === 'tenant'
                      ? setFormData({ ...formData, price_monthly: parseInt(e.target.value) || 0 })
                      : setGroupFormData({ ...groupFormData, price_monthly: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                    data-testid="plan-price-monthly"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Renewal Price (Monthly ₹)</label>
                  <input
                    type="number"
                    value={activeTab === 'tenant' ? formData.renewal_price_monthly : groupFormData.renewal_price_monthly}
                    onChange={(e) => activeTab === 'tenant'
                      ? setFormData({ ...formData, renewal_price_monthly: parseInt(e.target.value) || 0 })
                      : setGroupFormData({ ...groupFormData, renewal_price_monthly: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                    data-testid="plan-renewal-price-monthly"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Time Purchase Price (Yearly ₹)</label>
                  <input
                    type="number"
                    value={activeTab === 'tenant' ? formData.price_yearly : groupFormData.price_yearly}
                    onChange={(e) => activeTab === 'tenant'
                      ? setFormData({ ...formData, price_yearly: parseInt(e.target.value) || 0 })
                      : setGroupFormData({ ...groupFormData, price_yearly: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                    data-testid="plan-price-yearly"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Renewal Price (Yearly ₹)</label>
                  <input
                    type="number"
                    value={activeTab === 'tenant' ? formData.renewal_price_yearly : groupFormData.renewal_price_yearly}
                    onChange={(e) => activeTab === 'tenant'
                      ? setFormData({ ...formData, renewal_price_yearly: parseInt(e.target.value) || 0 })
                      : setGroupFormData({ ...groupFormData, renewal_price_yearly: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                    data-testid="plan-renewal-price-yearly"
                  />
                </div>
              </div>

              {/* GST Settings */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Percentage (%)</label>
                  <input
                    type="number"
                    value={activeTab === 'tenant' ? formData.gst_percent : groupFormData.gst_percent}
                    onChange={(e) => activeTab === 'tenant'
                      ? setFormData({ ...formData, gst_percent: parseFloat(e.target.value) || 0 })
                      : setGroupFormData({ ...groupFormData, gst_percent: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                    max="28"
                    step="0.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">Standard: 18%</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Included in Price?</label>
                  <div className="flex items-center gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={`gst_included_${activeTab}`}
                        checked={activeTab === 'tenant' ? !formData.gst_included : !groupFormData.gst_included}
                        onChange={() => activeTab === 'tenant'
                          ? setFormData({ ...formData, gst_included: false })
                          : setGroupFormData({ ...groupFormData, gst_included: false })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">No (Add GST extra)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={`gst_included_${activeTab}`}
                        checked={activeTab === 'tenant' ? formData.gst_included : groupFormData.gst_included}
                        onChange={() => activeTab === 'tenant'
                          ? setFormData({ ...formData, gst_included: true })
                          : setGroupFormData({ ...groupFormData, gst_included: true })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Yes (Inclusive)</span>
                    </label>
                  </div>
                </div>
                {/* GST Preview — both monthly & yearly */}
                <div className="col-span-2 mt-2 p-3 bg-white rounded border">
                  <p className="text-sm font-medium text-gray-700 mb-2">Price Preview:</p>
                  {(() => {
                    const priceM = activeTab === 'tenant' ? formData.price_monthly : groupFormData.price_monthly;
                    const renewalM = activeTab === 'tenant' ? formData.renewal_price_monthly : groupFormData.renewal_price_monthly;
                    const priceY = activeTab === 'tenant' ? formData.price_yearly : groupFormData.price_yearly;
                    const renewalY = activeTab === 'tenant' ? formData.renewal_price_yearly : groupFormData.renewal_price_yearly;
                    const gstPercent = activeTab === 'tenant' ? formData.gst_percent : groupFormData.gst_percent;
                    const gstIncluded = activeTab === 'tenant' ? formData.gst_included : groupFormData.gst_included;

                    const calcGst = (p) => {
                      const v = Number(p) || 0;
                      if (gstIncluded) {
                        const base = Math.round(v / (1 + gstPercent/100));
                        return { base, gst: v - base, total: v };
                      }
                      const gst = Math.round(v * gstPercent / 100);
                      return { base: v, gst, total: v + gst };
                    };
                    const fm = calcGst(priceM);
                    const rm = calcGst(renewalM);
                    const fy = calcGst(priceY);
                    const ry = calcGst(renewalY);

                    return (
                      <div className="text-xs space-y-3">
                        <div>
                          <p className="font-semibold text-gray-600 mb-1">Monthly</p>
                          <div className="pl-2 space-y-0.5">
                            <div><span className="text-gray-500">First Time: </span>₹{fm.base.toLocaleString('en-IN')} + GST ₹{fm.gst.toLocaleString('en-IN')} = <span className="font-bold text-emerald-600">₹{fm.total.toLocaleString('en-IN')}</span></div>
                            <div><span className="text-gray-500">Renewal: </span>₹{rm.base.toLocaleString('en-IN')} + GST ₹{rm.gst.toLocaleString('en-IN')} = <span className="font-bold text-indigo-600">₹{rm.total.toLocaleString('en-IN')}</span></div>
                          </div>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-600 mb-1">Yearly</p>
                          <div className="pl-2 space-y-0.5">
                            <div><span className="text-gray-500">First Time: </span>₹{fy.base.toLocaleString('en-IN')} + GST ₹{fy.gst.toLocaleString('en-IN')} = <span className="font-bold text-green-600">₹{fy.total.toLocaleString('en-IN')}</span></div>
                            <div><span className="text-gray-500">Renewal: </span>₹{ry.base.toLocaleString('en-IN')} + GST ₹{ry.gst.toLocaleString('en-IN')} = <span className="font-bold text-blue-600">₹{ry.total.toLocaleString('en-IN')}</span></div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {activeTab === 'tenant' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Active Students</label>
                      <input
                        type="number"
                        value={formData.max_students}
                        onChange={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">-1 = Unlimited</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Active Staff</label>
                      <input
                        type="number"
                        value={formData.max_staff}
                        onChange={(e) => setFormData({ ...formData, max_staff: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">-1 = Unlimited</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Yearly Admissions</label>
                      <input
                        type="number"
                        value={formData.max_yearly_admissions}
                        onChange={(e) => setFormData({ ...formData, max_yearly_admissions: parseInt(e.target.value) || -1 })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Per year limit. -1 = Unlimited</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Yearly Staff</label>
                      <input
                        type="number"
                        value={formData.max_yearly_staff}
                        onChange={(e) => setFormData({ ...formData, max_yearly_staff: parseInt(e.target.value) || -1 })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Per year limit. -1 = Unlimited</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Configurable Modules</label>
                      <input
                        type="number"
                        value={formData.max_configurable_modules}
                        onChange={(e) => setFormData({ ...formData, max_configurable_modules: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                        data-testid="plan-max-modules-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Extra modules a tenant can enable (Library, HR, Exam...). -1 = Unlimited, 0 = Fixed modules only</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Colleges</label>
                    <input
                      type="number"
                      value={groupFormData.max_colleges}
                      onChange={(e) => setGroupFormData({ ...groupFormData, max_colleges: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">-1 = Unlimited</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Active Students (per group)</label>
                      <input
                        type="number"
                        value={groupFormData.max_students}
                        onChange={(e) => setGroupFormData({ ...groupFormData, max_students: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">-1 = Unlimited</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Active Staff (per group)</label>
                      <input
                        type="number"
                        value={groupFormData.max_staff}
                        onChange={(e) => setGroupFormData({ ...groupFormData, max_staff: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">-1 = Unlimited</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Yearly Admissions (per group)</label>
                      <input
                        type="number"
                        value={groupFormData.max_yearly_admissions}
                        onChange={(e) => setGroupFormData({ ...groupFormData, max_yearly_admissions: parseInt(e.target.value) || -1 })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Per year. -1 = Unlimited</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Yearly Staff (per group)</label>
                      <input
                        type="number"
                        value={groupFormData.max_yearly_staff}
                        onChange={(e) => setGroupFormData({ ...groupFormData, max_yearly_staff: parseInt(e.target.value) || -1 })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Per year. -1 = Unlimited</p>
                    </div>
                  </div>
                </>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={activeTab === 'tenant' ? formData.is_active : groupFormData.is_active}
                  onChange={(e) => activeTab === 'tenant'
                    ? setFormData({ ...formData, is_active: e.target.checked })
                    : setGroupFormData({ ...groupFormData, is_active: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">Active (visible to users)</label>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 text-white rounded-lg ${
                    activeTab === 'tenant' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'
                  } disabled:opacity-50`}
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : (editingPlan ? 'Update Plan' : 'Create Plan')}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingPlan(null); }}
                  className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold">Delete Plan</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deletingPlan.name}</strong>? 
              This action cannot be undone.
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button
                onClick={() => { setShowDeleteModal(false); setDeletingPlan(null); }}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;
